import React from 'react';
import SEO from '../../components/SEO';
import SiteFooter from '../../components/SiteFooter';
import DirectAnswer from '../../components/DirectAnswer';
import { Link } from 'react-router-dom';
import { invisalignActonPageSEO } from '../../data/seo';
import { 
  getMedicalConditionLD, 
  getMedicalProcedureLD, 
  getBreadcrumbLD,
  getFAQPageLD 
} from '../../data/structuredData';
import { invisalignCondition, invisalignProcedure } from '../../data/medicalSchemas';
import { invisalignFAQs } from '../../data/faqs';
import { invisalignBreadcrumbs } from '../../data/breadcrumbs';
import { Button } from '../../components/ui/button';
import MiniConsultButton from '../../components/MiniConsultButton';
import EnquireButton from '../../components/EnquireButton';
import { 
  Phone, 
  Smile,
  CheckCircle2,
  Award,
  Clock,
  MapPin,
  Instagram,
  Facebook,
  Twitter,
  Music2
} from 'lucide-react';
import WhatsAppIcon from '@/components/WhatsAppIcon';

const InvisalignActon = () => {
  const phoneNumber = '07787814064';
  const whatsappNumber = '447787814064';
  const phoneLink = `tel:${phoneNumber}`;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const whatsappLink = isMobile
    ? `whatsapp://send?phone=${whatsappNumber}&text=Hi, I'd like to book an Invisalign consultation`
    : `https://web.whatsapp.com/send?phone=${whatsappNumber}&text=Hi, I'd like to book an Invisalign consultation`;

  return (
    <>
      {/* SEO Meta Tags & Structured Data */}
      <SEO
        title={invisalignActonPageSEO.title}
        description={invisalignActonPageSEO.description}
        path={invisalignActonPageSEO.path}
        keywords={invisalignActonPageSEO.keywords}
        image={invisalignActonPageSEO.image}
        structuredData={[
          getMedicalConditionLD(invisalignCondition),
          getMedicalProcedureLD(invisalignProcedure),
          getBreadcrumbLD(invisalignBreadcrumbs),
          getFAQPageLD(invisalignFAQs),
        ]}
      />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-sky-gray/20 shadow-sm">
        <div className="bg-gradient-to-r from-burnt-coral/5 to-sky-gray/10 border-b border-sky-gray/20">
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <a href={phoneLink} className="flex items-center gap-2 text-sm md:text-base text-rabbit hover:text-burnt-coral transition-colors group">
                <Phone className="w-4 h-4 md:w-5 md:h-5 text-burnt-coral group-hover:scale-110 transition-transform" />
                <span className="font-semibold">07787 814 064</span>
              </a>
              <div className="flex items-center gap-3 md:gap-4">
                <a href="https://www.instagram.com/kartikpateldentist/" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61588647254315" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://twitter.com/restore_dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on X (Twitter)">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://www.tiktok.com/@restore.dental" target="_blank" rel="noopener noreferrer" className="text-rabbit hover:text-burnt-coral transition-colors hover:scale-110 transform duration-200" aria-label="Follow us on TikTok">
                  <Music2 className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Smile className="w-8 h-8 text-burnt-coral" />
            <span className="text-xl md:text-2xl font-bold text-rabbit">restore.dental</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/">
              <Button variant="outline" size="sm" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white">
                Home
              </Button>
            </Link>
            <Button size="sm" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
              <a href={whatsappLink}>Book Now</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-gradient-to-br from-[#01A9B4]/10 via-white to-[#006A71]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-[#006A71]/10 rounded-full border-2 border-[#01A9B4]/20">
              <p className="text-sm font-medium text-[#006A71]">
                <MapPin className="w-4 h-4 inline mr-1" />
                Acton, West London W3
              </p>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-rabbit mb-6">
              Invisalign in <span className="text-burnt-coral">Acton</span>
            </h1>
            <p className="text-xl md:text-2xl text-chocolate-malt mb-8">
              Expert clear aligner treatment by Dr Kartik Patel • 150+ successful cases
            </p>
            <DirectAnswer
              question="How much does Invisalign cost in Acton and how long does it take?"
              answer="At Restore.Dental in Acton, Invisalign costs from £2,800 (Invisalign Lite) to £3,500–£5,500 for comprehensive cases, with retainers included and 0% finance from around £83/month. Most adults finish in 6–18 months. Dr Kartik Patel BDS has completed 150+ clear-aligner cases and plans every case with a 3D digital scan so you see your projected result before you start."
              bullets={['From £2,800 · 0% finance', '150+ cases by Dr Kartik Patel', '6–18 months typical', 'Free consultation & 3D smile simulation']}
              className="max-w-3xl mx-auto mb-8"
            />
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
                <a href={whatsappLink}>
                  <WhatsAppIcon className="w-5 h-5 mr-2" />
                  Free Consultation
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild>
                <a href={phoneLink}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
            <EnquireButton slug="invisalign" about="Invisalign" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="hero-enquire-btn" />
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-chocolate-malt">
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-burnt-coral" />
                <span>150+ Cases Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-burnt-coral" />
                <span>0% Finance Available</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-burnt-coral" />
                <span>Same-Day Appointments</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Why Choose Us */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                Why Choose restore.dental for Invisalign in Acton?
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Award className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Proven Expertise</h3>
                  <p className="text-chocolate-malt">Dr Kartik Patel has successfully completed over 150 Invisalign cases, with extensive experience in complex treatments.</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl border-2 border-[#01A9B4]/30">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-12 h-12 text-burnt-coral" />
                    <span className="bg-[#D4AF37] text-white text-xs font-bold px-2 py-1 rounded-full">PREMIUM TECH</span>
                  </div>
                  <h3 className="text-xl font-bold text-rabbit mb-2">Dental Monitoring™ Included</h3>
                  <p className="text-chocolate-malt">AI-powered remote monitoring for every patient. Track progress from home, fewer visits needed — at no extra cost.</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Clock className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">Convenient Location</h3>
                  <p className="text-chocolate-malt">Located in the heart of Acton, W3. Easy access from West London, Ealing, Chiswick, and Hammersmith.</p>
                </div>
                <div className="bg-gradient-to-br from-burnt-coral/5 to-sky-gray/5 p-6 rounded-xl">
                  <Smile className="w-12 h-12 text-burnt-coral mb-4" />
                  <h3 className="text-xl font-bold text-rabbit mb-2">0% Finance</h3>
                  <p className="text-chocolate-malt">Make your dream smile affordable with flexible payment plans and 0% finance options available.</p>
                </div>
              </div>
            </div>

            {/* Dental Monitoring - Premium Feature */}
            <div className="mb-16 bg-gradient-to-br from-[#01A9B4]/10 via-[#D4AF37]/5 to-white rounded-2xl p-8 md:p-12 border-2 border-[#01A9B4]/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[#01A9B4] text-white p-3 rounded-xl">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <div>
                  <span className="inline-block bg-[#D4AF37] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">INCLUDED FOR ALL PATIENTS</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-rabbit">
                    Dental Monitoring™ Remote Care
                  </h2>
                </div>
              </div>
              <p className="text-xl text-chocolate-malt mb-6 font-medium">
                We include cutting-edge AI-powered remote monitoring for every Invisalign patient at restore.dental — at no extra cost.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-xl p-6 border-2 border-[#01A9B4]/20">
                  <h3 className="text-xl font-bold text-rabbit mb-3 flex items-center gap-2">
                    <span className="text-2xl">📱</span> How It Works
                  </h3>
                  <ul className="space-y-2 text-chocolate-malt">
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span>Download the Dental Monitoring app to your smartphone</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span>Take quick 60-second scans at home (weekly)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span>AI analyzes 130+ data points from your scans</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span>Dr. Patel reviews your progress and sends feedback</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl p-6 border-2 border-[#01A9B4]/20">
                  <h3 className="text-xl font-bold text-rabbit mb-3 flex items-center gap-2">
                    <span className="text-2xl">✨</span> Your Benefits
                  </h3>
                  <ul className="space-y-2 text-chocolate-malt">
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span><strong>Fewer visits:</strong> Only come in when needed</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span><strong>Faster treatment:</strong> Early issue detection</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span><strong>Always supported:</strong> Message Dr. Patel anytime</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-burnt-coral mt-1">•</span>
                      <span><strong>Track progress:</strong> See your smile transformation</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#006A71]/10 rounded-xl p-6 border-l-4 border-[#01A9B4]">
                <p className="text-chocolate-malt">
                  <strong className="text-rabbit">Why this matters:</strong> Research shows Dental Monitoring improves patient compliance by 60%, reduces treatment time, and catches issues before they become problems. This technology is typically a premium add-on — but we believe every patient deserves the best care, so it's included free for all our Invisalign treatments.
                </p>
              </div>
            </div>

            {/* About Invisalign */}
            <div className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-6">
                What is Invisalign?
              </h2>
              <p className="text-lg text-chocolate-malt mb-4">
                Invisalign is the world's most advanced clear aligner system for straightening teeth. Unlike traditional metal braces, Invisalign uses virtually invisible, removable aligners custom-made for your teeth.
              </p>
              <p className="text-lg text-chocolate-malt mb-4">
                Each set of aligners gradually moves your teeth into the desired position. You'll change to a new set of aligners approximately every 1-2 weeks, and visit us every 6-8 weeks to monitor progress.
              </p>
              <h3 className="text-2xl font-bold text-rabbit mb-4 mt-8">Benefits of Invisalign:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <span className="text-chocolate-malt"><strong>Virtually Invisible</strong> - No one will know you're straightening your teeth</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <span className="text-chocolate-malt"><strong>Removable</strong> - Eat, drink, brush and floss normally</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <span className="text-chocolate-malt"><strong>Comfortable</strong> - No metal brackets or wires to irritate your mouth</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <span className="text-chocolate-malt"><strong>Predictable Results</strong> - See a 3D preview of your new smile before starting</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <span className="text-chocolate-malt"><strong>Fewer Visits</strong> - Check-ups every 6-8 weeks vs every 4 weeks with braces</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-burnt-coral flex-shrink-0 mt-1" />
                  <span className="text-chocolate-malt"><strong>Remote Monitoring</strong> - Track your progress from home with Dental Monitoring™</span>
                </li>
              </ul>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-r from-burnt-coral/10 via-sky-gray/10 to-[#D4AF37]/10 rounded-2xl p-8 md:p-12 text-center border-2 border-burnt-coral">
              <h2 className="text-3xl md:text-4xl font-bold text-rabbit mb-4">
                Ready for Your Free Invisalign Consultation?
              </h2>
              <p className="text-lg text-chocolate-malt mb-6">
                See a digital preview of your potential results. No obligation, completely free.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white" asChild>
                  <a href={whatsappLink}>
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    Book on WhatsApp
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" asChild>
                  <a href={phoneLink}>
                    <Phone className="w-5 h-5 mr-2" />
                    Call 07787 814 064
                  </a>
                </Button>
              <EnquireButton slug="invisalign" about="Invisalign" className="border-burnt-coral text-burnt-coral hover:bg-burnt-coral hover:text-white" testId="cta-enquire-btn" />
              </div>
            </div>

            {/* Other Services */}
            <div className="mt-16">
              <h2 className="text-2xl font-bold text-rabbit mb-6 text-center">
                Our Other Services in Acton
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">General Dentistry</h3>
                  <p className="text-sm text-chocolate-malt">Comprehensive dental care for all ages</p>
                </Link>
                <Link to="/implants-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Dental Implants</h3>
                  <p className="text-sm text-chocolate-malt">Permanent tooth replacement solution</p>
                </Link>
                <Link to="/composite-bonding-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Composite Bonding</h3>
                  <p className="text-sm text-chocolate-malt">Transform your smile in one visit</p>
                </Link>
                <Link to="/cosmetic-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Cosmetic Dentistry</h3>
                  <p className="text-sm text-chocolate-malt">Teeth whitening, veneers & more</p>
                </Link>
                <Link to="/emergency-dentist-acton" className="p-4 border-2 border-sky-gray/20 rounded-xl hover:border-burnt-coral hover:bg-burnt-coral/5 transition-all">
                  <h3 className="font-bold text-rabbit mb-2">Emergency Dentist</h3>
                  <p className="text-sm text-chocolate-malt">Same-day appointments available</p>
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      <SiteFooter />
      
      {/* Mini Consult NOW Floating Button */}
      <MiniConsultButton />
    </>
  );
};

export default InvisalignActon;
